import React, { createContext, ReactNode, useContext, useState, useEffect } from 'react';

import {
  useConfiguration,
  useWifiManagement,
  useInactivityDetection,
  useBrightness,
  type BaseStationConfig,
  type HardwareVersion,
  type IotStatus,
  type WifiNetworksResponse,
  type WifiStatusResponse,
} from '../hooks/firmware';

export interface FirmwareContextType {
  iotStatus: IotStatus | null;
  iotStatusLoading: boolean;
  iotStatusError: Error | null;

  baseStationConfig: BaseStationConfig | null;
  baseStationConfigLoading: boolean;

  wifiNetworks: WifiNetworksResponse['networks'];
  refreshWifiNetworks: () => Promise<void>;

  wifiStatus: WifiStatusResponse | null;
  wifiStatusLoading: boolean;
  refreshWifiStatus: () => Promise<void>;

  hardwareVersion: HardwareVersion | null;
  hardwareVersionLoading: boolean;

  // Method to refresh iotStatus
  refreshIotStatus: () => Promise<void>;

  hubName: string;

  // User inactivity state
  isIdle: boolean;

  // Lock screen computed properties
  isLocked: boolean;
  isPinSet: boolean;

  // Lock screen methods
  tryUnlock: (pin: string | null) => Promise<boolean>;
  lock: () => Promise<void>;
  setLockPin: (pin: string | null) => Promise<void>;

  // WiFi management methods
  connectWifi: (ssid: string, password: string) => Promise<void>;
  saveWifi: (ssid: string, password: string) => Promise<void>;
  disconnectWifi: () => Promise<void>;
  forgetWifi: (ssid: string) => Promise<void>;
}

export const FirmwareContext = createContext<FirmwareContextType | undefined>(undefined);

interface FirmwareProviderProps {
  children: ReactNode;
}

export const FirmwareProvider: React.FC<FirmwareProviderProps> = ({ children }) => {
  const [hubName, setHubName] = useState<string>('HG Home Guardian');

  // Use organized firmware hooks
  const deviceInfo = useConfiguration();
  const wifiManagement = useWifiManagement();

  const { dimBrightness, restoreBrightness } = useBrightness();
  const { isIdle } = useInactivityDetection(5 * 60); // 5 minutes

  useEffect(() => {
    const deviceName = deviceInfo.baseStationConfig.data?.device_name;
    if (deviceName) {
      setHubName(deviceName);
    }
  }, [deviceInfo.baseStationConfig.data]);

  useEffect(() => {
    if (isIdle) {
      dimBrightness();
      deviceInfo.setLockEnabled(true);
    } else {
      restoreBrightness();
    }
  }, [isIdle]);

  useEffect(() => {
    deviceInfo.iotStatus.execute();
    deviceInfo.baseStationConfig.execute();
    deviceInfo.hardwareVersion.execute();
    wifiManagement.networks.execute();
    wifiManagement.status.execute();
    const interval = setInterval(deviceInfo.iotStatus.execute, 2 * 60 * 1000); // every 2 minutes
    return () => clearInterval(interval);
  }, []);

  // Lock screen computed properties
  const isLocked = deviceInfo.baseStationConfig.data?.lock_enabled || false;
  const isPinSet =
    deviceInfo.baseStationConfig.data?.lock_pin != null &&
    deviceInfo.baseStationConfig.data?.lock_pin !== '';

  return (
    <FirmwareContext.Provider
      value={{
        iotStatus: deviceInfo.iotStatus.data,
        iotStatusLoading: deviceInfo.iotStatus.loading,
        iotStatusError: deviceInfo.iotStatus.error,
        baseStationConfig: deviceInfo.baseStationConfig.data,
        baseStationConfigLoading: deviceInfo.baseStationConfig.loading,
        wifiNetworks: wifiManagement.networks.data?.networks || [],
        refreshWifiNetworks: wifiManagement.networks.execute,
        wifiStatus: wifiManagement.status.data?.status || null,
        wifiStatusLoading: wifiManagement.status.loading,
        refreshWifiStatus: wifiManagement.status.execute,
        refreshIotStatus: deviceInfo.iotStatus.execute,
        hardwareVersion: deviceInfo.hardwareVersion.data,
        hardwareVersionLoading: deviceInfo.hardwareVersion.loading,
        hubName,
        isIdle,
        isLocked,
        isPinSet,
        setLockPin: deviceInfo.setLockPin,
        tryUnlock: async (pin: string | null = null) => {
          await deviceInfo.baseStationConfig.execute();
          if (!isPinSet) {
            await deviceInfo.setLockEnabled(false);
            return true;
          }
          const config = deviceInfo.baseStationConfig.data;
          if (config && config.lock_pin === pin) {
            await deviceInfo.setLockEnabled(false);
            return true;
          }
          return false;
        },
        lock: async () => {
          await deviceInfo.setLockEnabled(true);
        },
        connectWifi: async (ssid: string, password: string) => {
          await wifiManagement.connect(ssid, password);
        },
        saveWifi: async (ssid: string, password: string) => {
          await wifiManagement.save(ssid, password);
        },
        disconnectWifi: async () => {
          await wifiManagement.disconnect();
        },
        forgetWifi: async (ssid: string) => {
          await wifiManagement.forget(ssid);
        },
      }}>
      {children}
    </FirmwareContext.Provider>
  );
};

export const useFirmwareContext = (): FirmwareContextType => {
  const context = useContext(FirmwareContext);

  if (context === undefined) {
    throw new Error('useFirmwareContext must be used within a FirmwareProvider');
  }

  return context;
};
